import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ChevronDown, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { whatsappLink } from "../i18n/translations";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.25]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.35]);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/images/hero.png"
          alt="Golden Valley Résidences — vue aérienne du domaine"
          data-testid="hero-main-villa-photo"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-[#050505]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(5,5,5,0.75)_100%)]" />

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center"
      >
        <motion.div
          key={`badge-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="inline-flex items-center gap-2 self-start px-4 py-2 mb-8 border border-[rgba(197,160,89,0.4)] bg-black/40 backdrop-blur-sm"
          data-testid="hero-limited-badge"
        >
          <Sparkles size={13} className="text-[#C5A059]" />
          <span className="text-[11px] tracking-[0.24em] uppercase text-[#EED8A1]">{t.hero_badge}</span>
        </motion.div>

        <p
          data-testid="hero-eyebrow"
          className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6 overflow-hidden"
        >
          <motion.span
            key={`eyebrow-${lang}`}
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ delay: 0.35, duration: 0.8, ease: EASE }}
          >
            {t.hero_eyebrow}
          </motion.span>
        </p>

        <h1
          data-testid="hero-title"
          className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.08] text-[#F7F3E9] max-w-4xl"
        >
          {[t.hero_line1, t.hero_line2].map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "gold-text italic" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.5 + i * 0.18, duration: 1, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          key={`sub-${lang}`}
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#C7BFA7] font-light leading-relaxed"
        >
          {t.hero_sub}
        </motion.p>

        <motion.div
          key={`cta-${lang}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={whatsappLink(t.whatsapp_message)}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center gap-3 px-8 py-4 bg-[#C5A059] text-black text-sm font-semibold tracking-[0.14em] uppercase overflow-hidden hover:bg-[#EED8A1] transition-colors duration-300 pulse-gold"
          >
            <MessageCircle size={17} />
            <span>{t.hero_cta}</span>
          </a>
          <button
            data-testid="hero-discover-cta"
            onClick={() => window.__lenis?.scrollTo("#villas", { offset: -60 })}
            className="px-8 py-4 border border-[rgba(197,160,89,0.45)] text-[#EED8A1] text-sm tracking-[0.14em] uppercase hover:bg-[rgba(197,160,89,0.1)] transition-colors duration-300"
          >
            {t.hero_cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#8A8270]"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">{t.hero_scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown size={16} className="text-[#C5A059]" />
        </motion.div>
      </motion.div>
    </section>
  );
};
